import React from 'react';
import { DollarSign, Receipt, AlertTriangle } from 'lucide-react';

export default function ExpenseTrackerMockup({ size = 'sm' }) {
  const isLg = size === 'lg';

  const categories = [
    { name: 'Lumber & Framing', spent: 8200, budget: 10000, color: 'bg-amber-500', pct: 82 },
    { name: 'Plumbing', spent: 4800, budget: 5000, color: 'bg-blue-500', pct: 96 },
    { name: 'Electrical', spent: 3100, budget: 4500, color: 'bg-violet-500', pct: 69 },
    { name: 'HVAC', spent: 2900, budget: 3500, color: 'bg-cyan-500', pct: 83 },
  ];

  return (
    <div className={`w-full ${isLg ? 'p-4' : 'p-3'} bg-slate-800/80 rounded-xl`}>
      {/* Mini header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-amber-500/20 flex items-center justify-center">
            <DollarSign size={11} className="text-amber-400" />
          </div>
          <span className={`${isLg ? 'text-xs' : 'text-[10px]'} font-semibold text-white`}>
            Expense Tracker
          </span>
        </div>
        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-violet-500/15 rounded-md">
          <Receipt size={9} className="text-violet-400" />
          <span className="text-[8px] text-violet-300 font-medium">Scan</span>
        </div>
      </div>

      {/* Stat cards row */}
      <div className={`grid grid-cols-3 ${isLg ? 'gap-3 mb-4' : 'gap-1.5 mb-2.5'}`}>
        <div
          className={`bg-slate-700/50 rounded-lg ${isLg ? 'px-3 py-2' : 'px-2 py-1.5'} text-center`}
        >
          <p className="text-[7px] text-slate-500 uppercase tracking-wider">Total Spent</p>
          <p className={`${isLg ? 'text-sm' : 'text-[11px]'} font-extrabold text-white`}>$19,000</p>
        </div>
        <div
          className={`bg-slate-700/50 rounded-lg ${isLg ? 'px-3 py-2' : 'px-2 py-1.5'} text-center`}
        >
          <p className="text-[7px] text-slate-500 uppercase tracking-wider">Budget Left</p>
          <p className={`${isLg ? 'text-sm' : 'text-[11px]'} font-extrabold text-emerald-400`}>
            $23,000
          </p>
        </div>
        <div
          className={`bg-slate-700/50 rounded-lg ${isLg ? 'px-3 py-2' : 'px-2 py-1.5'} text-center`}
        >
          <p className="text-[7px] text-slate-500 uppercase tracking-wider">Categories</p>
          <p className={`${isLg ? 'text-sm' : 'text-[11px]'} font-extrabold text-blue-400`}>8</p>
        </div>
      </div>

      {/* Category bars */}
      <div className={`space-y-${isLg ? '2.5' : '1.5'}`}>
        {categories.slice(0, isLg ? 4 : 3).map((cat, i) => (
          <div key={i}>
            <div className="flex items-center justify-between mb-0.5">
              <span className={`${isLg ? 'text-[11px]' : 'text-[9px]'} text-slate-400 font-medium`}>
                {cat.name}
              </span>
              <div className="flex items-center gap-1.5">
                <span className={`${isLg ? 'text-[11px]' : 'text-[9px]'} font-semibold text-white`}>
                  ${cat.spent.toLocaleString()}
                </span>
                {cat.pct > 90 && <AlertTriangle size={9} className="text-red-400" />}
              </div>
            </div>
            <div
              className={`w-full ${isLg ? 'h-2' : 'h-1.5'} bg-slate-700/50 rounded-full overflow-hidden`}
            >
              <div
                className={`h-full ${cat.color} rounded-full transition-all duration-700`}
                style={{ width: `${cat.pct}%` }}
              />
            </div>
            <div className="flex justify-between mt-0.5">
              <span className="text-[7px] text-slate-600">{cat.pct}% used</span>
              <span className="text-[7px] text-slate-600">of ${cat.budget.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Alert banner (lg only) */}
      {isLg && (
        <div className="mt-3 flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <AlertTriangle size={12} className="text-red-400 flex-shrink-0" />
          <p className="text-[10px] text-red-300">Plumbing is at 96% of budget — $200 remaining</p>
        </div>
      )}
    </div>
  );
}
